/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Send } from "lucide-react";
import PhotoUploadButton from "@/components/PhotoUploadButton";
import FullScreenImage from "@/components/FullScreenImage";
import MentionComposerField from "@/components/MentionComposerField";
import { dispatchMentionNotifications, searchAllMentionSuggestions } from "@/lib/mentions";
import { resolveSignedStorageUrls, resolveStoredObjectUrl } from "@/lib/storageObjectUrls";
import type { MentionDraft } from "../../shared/community";
import { AppButton, AppButtonRow, AppCard, AppCardBody, AppSection, AppSectionHeader, AppText } from "../../ui-foundation";

interface EventChatMessage {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  photo_url?: string | null;
  profiles?: { display_name: string | null; avatar_url: string | null } | null;
}

interface EventChatProps {
  roomId: string;
  eventId: string;
  eventTitle: string;
}

const EventChat = ({ roomId, eventId, eventTitle }: EventChatProps) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<EventChatMessage[]>([]);
  const [newMsg, setNewMsg] = useState("");
  const [newMsgMentions, setNewMsgMentions] = useState<MentionDraft[]>([]);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [resolvedPhotoUrls, setResolvedPhotoUrls] = useState<Map<string, string>>(new Map());
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchMessages = useCallback(async () => {
    const { data } = await supabase
      .from("event_chat_messages" as any)
      .select("*, profiles(display_name, avatar_url)")
      .eq("room_id", roomId)
      .order("created_at", { ascending: true });
    if (data) setMessages(data as unknown as EventChatMessage[]);
  }, [roomId]);

  useEffect(() => {
    fetchMessages();
    const channel = supabase
      .channel(`event-chat-${roomId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "event_chat_messages", filter: `room_id=eq.${roomId}` }, () => fetchMessages())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchMessages, roomId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    let active = true;
    const photoValues = messages.map((message) => message.photo_url).filter(Boolean);

    void resolveSignedStorageUrls("channel-photos", photoValues).then((nextMap) => {
      if (active) {
        setResolvedPhotoUrls(nextMap);
      }
    });

    return () => {
      active = false;
    };
  }, [messages]);

  const send = async () => {
    if ((!newMsg.trim() && !photoUrl) || !user || sending) return;
    const content = newMsg.trim();
    setSending(true);
    const { error } = await supabase.from("event_chat_messages" as any).insert({
      room_id: roomId, user_id: user.id, content: content || "📷", photo_url: photoUrl,
    } as any);

    if (error) {
      console.error("Could not send event chat message", error);
      setSending(false);
      return;
    }

    await dispatchMentionNotifications({
      actorId: user.id,
      actorDisplayName:
        user.user_metadata?.display_name ?? user.email?.split("@")[0] ?? "Someone",
      content,
      mentions: newMsgMentions,
      bodyContext: `in ${eventTitle || "an event chat"}`,
      entityType: "event",
      entityId: eventId,
      url: `/whats-on/${eventId}`,
    });
    setNewMsg("");
    setNewMsgMentions([]);
    setPhotoUrl(null);
    setSending(false);
  };

  return (
    <AppSection gap="$md">
      <AppSectionHeader title="Event Chat" subtitle="Keep practical event plans in one place." />
      <AppCard backgroundColor="$background">
        <AppCardBody gap="$md">
          <div className="max-h-64 space-y-3 overflow-y-auto">
            {messages.length === 0 && (
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md" textAlign="center" py="$lg">
                No messages yet — say hello!
              </AppText>
            )}
            {messages.map((msg) => (
              <div key={msg.id} className="flex gap-2">
                <Avatar className="h-7 w-7 shrink-0">
                  {msg.profiles?.avatar_url && <AvatarImage src={msg.profiles.avatar_url} />}
                  <AvatarFallback className="text-xs bg-secondary text-secondary-foreground">
                    {(msg.profiles?.display_name ?? "K")[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <AppText className="font-medium" fontSize="$xs" lineHeight="$xs">
                      {msg.profiles?.display_name ?? "Member"}
                    </AppText>
                    <AppText color="$colorMuted" fontSize={10} lineHeight={10}>
                      {new Date(msg.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                    </AppText>
                  </div>
                  <AppText fontSize="$sm" lineHeight="$md">{msg.content}</AppText>
                  {msg.photo_url && resolveStoredObjectUrl("channel-photos", msg.photo_url, resolvedPhotoUrls) ? (
                    <button
                      onClick={() => setFullScreenImage(resolveStoredObjectUrl("channel-photos", msg.photo_url, resolvedPhotoUrls))}
                      className="mt-1 max-w-[160px] rounded-lg overflow-hidden"
                    >
                      <img src={resolveStoredObjectUrl("channel-photos", msg.photo_url, resolvedPhotoUrls) ?? ""} alt="" className="w-full rounded-lg" />
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        </AppCardBody>
      </AppCard>
      <PhotoUploadButton
        bucket="channel-photos"
        onPhotoReady={setPhotoUrl}
        onClear={() => setPhotoUrl(null)}
        previewUrl={photoUrl}
        returnObjectPath
      />
      <AppButtonRow flexWrap="nowrap">
        <MentionComposerField
          value={newMsg}
          onChangeText={setNewMsg}
          mentions={newMsgMentions}
          onMentionsChange={setNewMsgMentions}
          lookupCandidates={(query) => searchAllMentionSuggestions(query, user?.id)}
          placeholder="Type a message..."
          disabled={sending}
          fieldProps={{ flex: 1 }}
          inputProps={{
            flex: 1,
            onKeyDown: (event: any) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void send();
              }
            },
          }}
        />
        <AppButton type="button" size="sm" onPress={() => void send()} disabled={sending || (!newMsg.trim() && !photoUrl)}>
          <Send size={16} />
          {sending ? "Sending..." : "Send"}
        </AppButton>
      </AppButtonRow>
      {fullScreenImage && <FullScreenImage images={[fullScreenImage]} currentIndex={0} onClose={() => setFullScreenImage(null)} onNavigate={() => {}} />}
    </AppSection>
  );
};

export default EventChat;
